// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>

import type { ClimateData, Station } from '../types';
import { lineChart, ticks } from '../charts';
import { attachSvgZoom } from '../svgZoom';
import { fmtRate, fmtSigned, esc } from '../format';
import { gloss } from '../glossary';
import { openDrawer } from '../drawer';
import { sectionHead, statTile } from './ui';

export function mount(el: HTMLElement, data: ClimateData): void {
  const { national, meta, stations } = data;
  const dayPts = national.annual.filter((r) => r.aMax != null).map((r) => ({ x: r.y, y: r.aMax as number }));
  const nightPts = national.annual.filter((r) => r.aMin != null).map((r) => ({ x: r.y, y: r.aMin as number }));
  const ally = [...dayPts, ...nightPts].map((p) => p.y);
  const pts = stations.filter((s) => s.rateMax != null && s.rateMin != null) as Array<Station & { rateMax: number; rateMin: number }>;
  const nightsFaster = pts.filter((s) => s.rateMin > s.rateMax).length;

  el.innerHTML = `
    <section class="panel">
      ${sectionHead('Nights are warming faster than days', `Overnight lows (${gloss('tmin', 'minimum temperature')}) are rising faster than daytime highs (${gloss('tmax', 'maximum temperature')}). Warmer nights matter for health, sleep, cities and ecosystems that rely on cooling down.`)}
      <div class="tiles">
        ${statTile(fmtRate(meta.nationalRateMin), 'Nights warming', 'national overnight-low trend')}
        ${statTile(fmtRate(meta.nationalRateMax), 'Days warming', 'national daytime-high trend')}
        ${statTile(`${nightsFaster} / ${pts.length}`, 'Stations: nights faster', 'more than half warm faster at night')}
      </div>
      <div class="chart">${lineChart([
        { name: 'Nights', color: '#3b6fb0', points: nightPts, width: 1.8 },
        { name: 'Days', color: '#d9614a', points: dayPts, width: 1.8 },
      ], { w: 1000, h: 320, yLabel: '°C vs 1961–1990', zeroLine: true, yMin: Math.min(...ally) - 0.1, yMax: Math.max(...ally) + 0.1, hoverYears: true, tipFor: (yr) => {
        const r = national.annual.find((x) => x.y === yr);
        return r ? `${yr}: nights ${fmtSigned(r.aMin)}, days ${fmtSigned(r.aMax)}` : `${yr}`;
      } })}</div>
      <div class="legend-row"><span class="lg-line" style="background:#3b6fb0"></span>Nights (overnight lows)<span class="lg-line" style="background:#d9614a"></span>Days (afternoon highs)</div>
    </section>

    <section class="panel">
      ${sectionHead('Every station: night warming vs day warming', 'Each dot is a station. Above the grey line, nights are warming faster than days — where most of the country sits. Hover for the rates; click to open the record. Scroll to zoom.')}
      <div class="scatter-wrap" data-scatter></div>
    </section>`;

  const wrap = el.querySelector('[data-scatter]') as HTMLElement;
  wrap.innerHTML = scatter(pts);
  const svg = wrap.querySelector('svg') as SVGSVGElement | null;
  if (svg) attachSvgZoom(svg);
  wrap.addEventListener('click', (e) => {
    const c = (e.target as Element).closest('[data-station]') as Element | null;
    if (c) openDrawer(data, c.getAttribute('data-station')!);
  });
}

function scatter(pts: Array<Station & { rateMax: number; rateMin: number }>): string {
  const w = 720, h = 560, m = { t: 20, r: 20, b: 52, l: 56 };
  const iw = w - m.l - m.r, ih = h - m.t - m.b;
  const vals = pts.flatMap((s) => [s.rateMax, s.rateMin]);
  const lo = Math.min(0, ...vals), hi = Math.max(...vals) + 0.02;
  const sx = (v: number) => m.l + ((v - lo) / (hi - lo)) * iw;
  const sy = (v: number) => m.t + ih - ((v - lo) / (hi - lo)) * ih;
  let g = '';
  for (const t of ticks(lo, hi, 5)) {
    g += `<line x1="${sx(t).toFixed(1)}" y1="${m.t}" x2="${sx(t).toFixed(1)}" y2="${m.t + ih}" class="grid"/>`;
    g += `<line x1="${m.l}" y1="${sy(t).toFixed(1)}" x2="${m.l + iw}" y2="${sy(t).toFixed(1)}" class="grid"/>`;
    g += `<text x="${sx(t).toFixed(1)}" y="${h - 32}" class="axis-lbl" text-anchor="middle">${t.toFixed(2)}</text>`;
    g += `<text x="${m.l - 8}" y="${(sy(t) + 3).toFixed(1)}" class="axis-lbl" text-anchor="end">${t.toFixed(2)}</text>`;
  }
  // y=x diagonal (equal warming)
  g += `<line x1="${sx(lo).toFixed(1)}" y1="${sy(lo).toFixed(1)}" x2="${sx(hi).toFixed(1)}" y2="${sy(hi).toFixed(1)}" class="diag"/>`;
  g += `<text x="${sx(hi) - 6}" y="${sy(hi) + 16}" class="axis-lbl diag-lbl" text-anchor="end">equal warming</text>`;
  for (const s of pts) {
    const faster = s.rateMin > s.rateMax;
    g += `<circle cx="${sx(s.rateMax).toFixed(1)}" cy="${sy(s.rateMin).toFixed(1)}" r="4.2" fill="${faster ? '#b52a1f' : '#3b6fb0'}" fill-opacity="0.78" stroke="#fff" stroke-width="0.6" class="dot" data-station="${s.id}" data-tip="${esc(s.name)} (${s.state}) · nights ${fmtRate(s.rateMin)} · days ${fmtRate(s.rateMax)}"/>`;
  }
  g += `<text x="${m.l + iw / 2}" y="${h - 12}" class="axis-title" text-anchor="middle">Day warming (°C/decade) →</text>`;
  g += `<text x="16" y="${m.t + ih / 2}" class="axis-title" text-anchor="middle" transform="rotate(-90 16 ${m.t + ih / 2})">Night warming (°C/decade) →</text>`;
  return `<svg viewBox="0 0 ${w} ${h}" class="scatter-svg" role="img" aria-label="Night vs day warming scatter">${g}</svg>`;
}
